import { addObject3DComponent } from "../utils/jsx-entity";
import { NetworkedMediaFrame, MediaFrame, Networked } from "../bit-components";
import { addComponent, addEntity, hasComponent } from "bitecs";
import { MediaType } from "../utils/media-utils";
import { COLLISION_LAYERS } from "../constants";
import { Layers } from "../camera-layers";
import {
  mediaFrame2dTexture,
  mediaFrame2dTextureSquare,
  mediaFrame3dTextureLandscape,
  mediaFrame3dTextureSquare,
  mediaFrame3dTexturePortrait
} from "../utils/media-frame-textures";
import { inflateRigidBody, Type } from "./rigid-body";
import { Fit, inflatePhysicsShape, Shape } from "./physics-shape";
import { Mesh, ShaderMaterial, Color, DoubleSide, BoxGeometry } from "three";

const DEFAULTS = {
  bounds: { x: 1, y: 1, z: 1 },
  mediaType: "all"
};
export function inflateMediaFrame(world, eid, componentProps) {
  componentProps = Object.assign({}, DEFAULTS, componentProps);
  const guide = new Mesh(
    new BoxGeometry(componentProps.bounds.x, componentProps.bounds.y, componentProps.bounds.z),
    new ShaderMaterial({
      uniforms: {
        color: { value: new Color(0xfe12a8) }
      },
      vertexShader: `
            varying vec2 vUv;
            void main()
            {
              gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
              vUv = uv;
            }
          `,
      fragmentShader: `
            // adapted from https://www.shadertoy.com/view/Mlt3z8
            float bayerDither2x2( vec2 v ) {
              return mod( 3.0 * v.y + 2.0 * v.x, 4.0 );
            }
            float bayerDither4x4( vec2 v ) {
              vec2 P1 = mod( v, 2.0 );
              vec2 P2 = mod( floor( 0.5  * v ), 2.0 );
              return 4.0 * bayerDither2x2( P1 ) + bayerDither2x2( P2 );
            }

            varying vec2 vUv;
            uniform vec3 color;
            void main() {
              float alpha = max(step(0.45, abs(vUv.x - 0.5)), step(0.45, abs(vUv.y - 0.5))) - 0.5;
              if( ( bayerDither4x4( floor( mod( gl_FragCoord.xy, 4.0 ) ) ) ) / 16.0 >= alpha ) discard;
              gl_FragColor = vec4(color, 1.0);
            }
          `,
      side: DoubleSide
    })
  );
  guide.layers.set(Layers.CAMERA_LAYER_UI);
  // TODO: This is a hack around the physics system addBody call requiring its body to have parent
  guide.parent = new THREE.Group();
  const guideEid = addEntity(world);
  addObject3DComponent(world, guideEid, guide);
  addComponent(world, MediaFrame, eid, true);
  addComponent(world, NetworkedMediaFrame, eid, true);

  if (!hasComponent(world, Networked, eid)) addComponent(world, Networked, eid);

  MediaFrame.mediaType[eid] = {
    all: MediaType.ALL,
    "all-2d": MediaType.ALL_2D,
    model: MediaType.MODEL,
    image: MediaType.IMAGE,
    video: MediaType.VIDEO,
    pdf: MediaType.PDF
  }[componentProps.mediaType];
  MediaFrame.bounds[eid].set([componentProps.bounds.x, componentProps.bounds.y, componentProps.bounds.z]);
  MediaFrame.guide[eid] = guideEid;

  const mediaFrame2dTextures = {
    landscape: mediaFrame2dTexture,
    portrait: mediaFrame2dTextureSquare,
    square: mediaFrame2dTextureSquare
  };

  const mediaFrame3dTextures = {
    landscape: mediaFrame3dTextureLandscape,
    portrait: mediaFrame3dTexturePortrait,
    square: mediaFrame3dTextureSquare
  };

  const getMediaFrameTexture = textures => {
    if (componentProps.bounds.x > componentProps.bounds.y) {
      return textures.landscape;
    }

    if (componentProps.bounds.x < componentProps.bounds.y) {
      return textures.portrait;
    }

    return textures.square;
  };

  const sameBoundsPlaceholder = Math.max(componentProps.bounds.x, componentProps.bounds.y);
  const mediaFramePlaceholderGeometry = new THREE.PlaneGeometry(sameBoundsPlaceholder, sameBoundsPlaceholder);
  const mediaFramePlaceholderMaterial = new THREE.MeshBasicMaterial({
    map:
      componentProps.mediaType === "model"
        ? getMediaFrameTexture(mediaFrame3dTextures)
        : getMediaFrameTexture(mediaFrame2dTextures),
    side: THREE.DoubleSide
  });

  const mediaFramePlaceholder = new THREE.Mesh(mediaFramePlaceholderGeometry, mediaFramePlaceholderMaterial);
  mediaFramePlaceholder.material.transparent = true;

  mediaFramePlaceholder.position.set(0, 0, -0.01);
  mediaFramePlaceholder.name = "media-frame-image";

  guide.add(mediaFramePlaceholder);

  inflateRigidBody(world, eid, {
    type: Type.KINEMATIC,
    collisionGroup: COLLISION_LAYERS.MEDIA_FRAMES,
    collisionMask: COLLISION_LAYERS.INTERACTABLES,
    disableCollision: true
  });
  inflatePhysicsShape(world, eid, {
    type: Shape.BOX,
    fit: Fit.MANUAL,
    halfExtents: [componentProps.bounds.x / 2, componentProps.bounds.y / 2, componentProps.bounds.z / 2]
  });
}
